import {
  Building2,
  Check,
  ChevronDown,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getCustomers } from '@/modules/customer/api/customer.api'
import { toast } from '@/shared/lib/toast'

const formatDateForInput = (val) => {
  if (!val) return new Date().toISOString().split('T')[0]
  try {
    return new Date(val).toISOString().split('T')[0]
  } catch {
    return new Date().toISOString().split('T')[0]
  }
}

const EMAIL_VALIDATION_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i

const createEmptyCall = (index = 1) => ({
  id: `${Date.now()}-${Math.random()}`,
  callNumber: index,
  selectedCustomerId: null,
  customerName: '',
  contactPersonName: '',
  phoneNumber: '',
  email: '',
  remark: '',
})

export default function EditDCRModal(props) {
  const { dcr, open } = props

  if (!open || !dcr) return null

  return <EditDCRForm key={dcr._id} {...props} />
}

function EditDCRForm({ _open, onOpenChange, dcr, onSubmit, isSubmitting }) {
  const [date, setDate] = useState(() =>
    formatDateForInput(dcr?.date || dcr?.createdAt),
  )

  const [calls, setCalls] = useState(() => {
    if (dcr?.calls && Array.isArray(dcr.calls) && dcr.calls.length > 0) {
      return dcr.calls.map((c, idx) => ({
        id: c._id ? String(c._id) : `${Date.now()}-${idx}`,
        callNumber: c.callNumber || idx + 1,
        selectedCustomerId: null,
        customerName: c.customerName || dcr.customerName || '',
        contactPersonName: c.contactPersonName || dcr.contactPersonName || '',
        phoneNumber: c.phoneNumber || dcr.phoneNumber || '',
        email: c.email || dcr.email || '',
        remark: c.remark || dcr.remarks || '',
      }))
    }

    return [
      {
        id: `${Date.now()}-0`,
        callNumber: 1,
        selectedCustomerId: null,
        customerName: dcr?.customerName || '',
        contactPersonName: dcr?.contactPersonName || '',
        phoneNumber: dcr?.phoneNumber || '',
        email: dcr?.email || '',
        remark: dcr?.remarks || '',
      },
    ]
  })

  const [customers, setCustomers] = useState([])
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)
  const [activeDropdownCallId, setActiveDropdownCallId] = useState(null)
  const [customerSearch, setCustomerSearch] = useState('')

  const dropdownContainerRef = useRef(null)

  // Fetch customers for the dropdown
  useEffect(() => {
    let isMounted = true

    const loadCustomers = async () => {
      try {
        setIsLoadingCustomers(true)
        const res = await getCustomers({ limit: 100 })
        if (!isMounted) return

        const list = res.data?.customers || res.customers || []
        setCustomers(list)
      } catch (err) {
        console.error('Failed to fetch customers for Edit DCR dropdown:', err)
      } finally {
        if (isMounted) {
          setIsLoadingCustomers(false)
        }
      }
    }

    loadCustomers()

    return () => {
      isMounted = false
    }
  }, [])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownContainerRef.current &&
        !dropdownContainerRef.current.contains(event.target)
      ) {
        setActiveDropdownCallId(null)
      }
    }

    if (activeDropdownCallId) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeDropdownCallId])

  // Filter customers
  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase()
    if (!q) return customers

    return customers.filter(
      (c) =>
        (c.companyName || '').toLowerCase().includes(q) ||
        (c.contactPersonName || '').toLowerCase().includes(q) ||
        (c.contactPersonPhone || '').toLowerCase().includes(q) ||
        (c.contactPersonEmail || '').toLowerCase().includes(q),
    )
  }, [customers, customerSearch])

  // Add Call
  const handleAddCall = () => {
    const newIndex = calls.length + 1
    const newCall = createEmptyCall(newIndex)
    setCalls((prev) => [...prev, newCall])
    setActiveDropdownCallId(newCall.id)
    setCustomerSearch('')
  }

  // Remove Call
  const handleRemoveCall = (callId) => {
    if (calls.length <= 1) {
      toast.error('A DCR must have at least one call')
      return
    }

    setCalls((prev) =>
      prev
        .filter((c) => c.id !== callId)
        .map((c, idx) => ({
          ...c,
          callNumber: idx + 1,
        })),
    )

    if (activeDropdownCallId === callId) {
      setActiveDropdownCallId(null)
    }
  }

  // Toggle Dropdown
  const handleToggleDropdown = (callId) => {
    setActiveDropdownCallId((prev) => (prev === callId ? null : callId))
    setCustomerSearch('')
  }

  // Select customer from dropdown
  const handleSelectCustomer = (callId, customer) => {
    setCalls((prev) =>
      prev.map((c) => {
        if (c.id === callId) {
          return {
            ...c,
            selectedCustomerId: customer._id || customer.id,
            customerName: customer.companyName || '',
            contactPersonName: customer.contactPersonName || '',
            phoneNumber: (customer.contactPersonPhone || '')
              .replace(/\D/g, '')
              .slice(0, 10),
            email: (customer.contactPersonEmail || '').toLowerCase(),
          }
        }
        return c
      }),
    )

    setActiveDropdownCallId(null)
    setCustomerSearch('')
  }

  // Field change handler
  const handleCallChange = (callId, field, value) => {
    let sanitized = value
    if (field === 'customerName') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
    } else if (field === 'contactPersonName') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
    } else if (field === 'phoneNumber') {
      sanitized = value.replace(/\D/g, '').slice(0, 10)
    }

    setCalls((prev) =>
      prev.map((c) => {
        if (c.id === callId) {
          return {
            ...c,
            [field]: sanitized,
          }
        }
        return c
      }),
    )
  }

  // Submit Edit Form
  const handleSubmit = async (event) => {
    event.preventDefault()

    if (calls.length === 0) {
      toast.error('Please add at least one call to the DCR')
      return
    }

    // Validation
    for (let i = 0; i < calls.length; i++) {
      const c = calls[i]
      const callNum = i + 1

      if (!c.customerName?.trim()) {
        toast.error(`Call ${callNum}: Company name is required`)
        return
      }

      if (c.phoneNumber?.trim() && !/^\d{10}$/.test(c.phoneNumber.trim())) {
        toast.error(
          `Call ${callNum}: Phone number must be exactly 10 numeric digits`,
        )
        return
      }

      if (c.email?.trim() && !EMAIL_VALIDATION_REGEX.test(c.email.trim())) {
        toast.error(`Call ${callNum}: Please enter a valid email address`)
        return
      }
    }

    const firstCall = calls[0]
    const formattedCalls = calls.map((c, idx) => ({
      callNumber: idx + 1,
      customerName: c.customerName.trim(),
      contactPersonName: c.contactPersonName.trim(),
      phoneNumber: c.phoneNumber.trim(),
      email: c.email.trim().toLowerCase(),
      remark: c.remark.trim(),
    }))

    const payload = {
      date: date || undefined,
      customerName: firstCall.customerName.trim(),
      contactPersonName: firstCall.contactPersonName.trim(),
      phoneNumber: firstCall.phoneNumber.trim(),
      email: firstCall.email.trim().toLowerCase(),
      remarks: firstCall.remark.trim(),
      calls: formattedCalls,
    }

    try {
      await onSubmit(dcr._id, payload)
      onOpenChange(false)
    } catch {
      // Error handled by store
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    onOpenChange(false)
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4 backdrop-blur-xs">
        <div
          ref={dropdownContainerRef}
          className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl sm:rounded-3xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-5 py-5 sm:px-8">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Edit DCR</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Update customer visit records and all call details.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            {/* Top Toolbar: Date & Add Call */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-slate-50/70 px-5 py-4 sm:px-8">
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Visit Date:
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 outline-none focus:border-slate-500"
                />
              </div>

              <button
                type="button"
                onClick={handleAddCall}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-950 px-4 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-slate-800"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Call
              </button>
            </div>

            {/* Calls List */}
            <div className="flex-1 space-y-5 overflow-y-auto p-5 sm:p-8">
              {calls.map((call, index) => {
                const isDropdownOpen = activeDropdownCallId === call.id

                return (
                  <div
                    key={call.id}
                    className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition hover:border-slate-300"
                  >
                    {/* Call Card Header */}
                    <div className="mb-4 flex items-center justify-between border-b pb-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-xs font-bold text-blue-700">
                          {index + 1}
                        </span>
                        <h3 className="text-sm font-bold text-slate-900">
                          Call {index + 1}
                        </h3>
                      </div>

                      {calls.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveCall(call.id)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                          title="Remove this call"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Customer Selection Row */}
                    <div className="mb-4">
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Select Existing Customer (Auto-fetch)
                      </label>

                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => handleToggleDropdown(call.id)}
                          className="flex h-11 w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 px-4 text-left text-sm text-slate-700 transition hover:bg-slate-50 focus:border-blue-500 focus:bg-white focus:outline-none"
                        >
                          <span className="flex items-center gap-2 truncate">
                            <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                            {call.customerName ? (
                              <span className="font-semibold text-slate-900">
                                {call.customerName}
                              </span>
                            ) : (
                              <span className="text-slate-400">
                                Choose a customer from database...
                              </span>
                            )}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 text-slate-400 transition-transform ${
                              isDropdownOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {/* Customer Search Dropdown */}
                        {isDropdownOpen && (
                          <div className="absolute left-0 top-full z-30 mt-1.5 w-full rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                            {/* Search & Add Customer */}
                            <div className="mb-2 flex items-center gap-2">
                              <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                                <input
                                  type="text"
                                  autoFocus
                                  value={customerSearch}
                                  onChange={(e) =>
                                    setCustomerSearch(e.target.value)
                                  }
                                  placeholder="Search customer by name or phone..."
                                  className="w-full rounded-xl border border-slate-200 py-1.5 pl-8 pr-3 text-xs outline-none focus:border-blue-500"
                                />
                              </div>
                            </div>

                            {/* Dropdown Options */}
                            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                              {isLoadingCustomers ? (
                                <div className="py-6 text-center text-xs text-slate-400">
                                  <Loader2 className="mx-auto h-4 w-4 animate-spin mb-1 text-slate-400" />
                                  Loading customers...
                                </div>
                              ) : filteredCustomers.length === 0 ? (
                                <div className="py-4 text-center text-xs text-slate-400">
                                  No customers found matching &quot;
                                  {customerSearch}&quot;
                                </div>
                              ) : (
                                filteredCustomers.map((cust) => {
                                  const isSelected =
                                    call.customerName === cust.companyName

                                  return (
                                    <div
                                      key={cust._id || cust.id}
                                      onClick={() =>
                                        handleSelectCustomer(call.id, cust)
                                      }
                                      className={`flex cursor-pointer items-center justify-between p-2.5 rounded-lg text-xs transition hover:bg-slate-50 ${
                                        isSelected
                                          ? 'bg-blue-50/60 font-semibold text-blue-900'
                                          : 'text-slate-700'
                                      }`}
                                    >
                                      <div>
                                        <p className="font-semibold text-slate-900">
                                          {cust.companyName}
                                        </p>
                                        <p className="text-[11px] text-slate-500">
                                          {cust.contactPersonName ||
                                            'No contact'}{' '}
                                          {cust.contactPersonPhone
                                            ? `• ${cust.contactPersonPhone}`
                                            : ''}
                                        </p>
                                      </div>

                                      {isSelected && (
                                        <Check className="h-4 w-4 text-blue-600" />
                                      )}
                                    </div>
                                  )
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Inputs Grid */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {/* Company Name */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={call.customerName}
                          onChange={(e) =>
                            handleCallChange(
                              call.id,
                              'customerName',
                              e.target.value,
                            )
                          }
                          placeholder="Company name"
                          className="h-10 w-full rounded-xl border border-slate-200 px-3.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Contact Person */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                          Contact Person
                        </label>
                        <input
                          type="text"
                          value={call.contactPersonName}
                          onChange={(e) =>
                            handleCallChange(
                              call.id,
                              'contactPersonName',
                              e.target.value,
                            )
                          }
                          placeholder="Contact person name"
                          className="h-10 w-full rounded-xl border border-slate-200 px-3.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          maxLength={10}
                          value={call.phoneNumber}
                          onChange={(e) =>
                            handleCallChange(
                              call.id,
                              'phoneNumber',
                              e.target.value,
                            )
                          }
                          placeholder="10-digit phone number"
                          className="h-10 w-full rounded-xl border border-slate-200 px-3.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                          Email
                        </label>
                        <input
                          type="email"
                          value={call.email}
                          onChange={(e) =>
                            handleCallChange(call.id, 'email', e.target.value)
                          }
                          placeholder="Email address"
                          className="h-10 w-full rounded-xl border border-slate-200 px-3.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Remark */}
                      <div className="sm:col-span-2">
                        <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                          Remark
                        </label>
                        <textarea
                          rows={2}
                          value={call.remark}
                          onChange={(e) =>
                            handleCallChange(call.id, 'remark', e.target.value)
                          }
                          placeholder="Call details and discussion notes..."
                          className="w-full resize-none rounded-xl border border-slate-200 p-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-3 border-t bg-slate-50/50 px-5 py-4 sm:px-8">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="rounded-full border border-slate-200 px-6 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-7 py-2.5 text-sm font-semibold text-white shadow-xs transition hover:bg-slate-800 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating DCR...
                  </>
                ) : (
                  'Update DCR'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
